import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import i18n from '../../i18n';
import { getBrandLocale } from './brandCurrency';
import { formatPreviewPaymentMethod } from '../i18n/documentPreviewLabels';
import { SAUDI_BANK_CODES } from './bankingHelpers';
import { getFacturationDocumentPrefix } from '../../pages/facturation/facturationConfigDefaults';

const PDF_DOCUMENT_PREFIX_FALLBACKS = {
  invoice: 'FAC',
  proforma: 'PRO',
  'quote-request': 'DD',
  quote: 'DEV',
  'proforma-request': 'DPR',
  'purchase-order': 'BC',
  'delivery-note': 'BL',
  'shipping-slip': 'BE',
  'reception-note': 'BRL',
  'return-slip': 'BRF',
  'stock-sheet': 'FS',
  'material-exit': 'BL',
  'transfer-note': 'BT',
  'exit-note': 'BS',
  'stock-entry': 'EN',
  'payment-voucher': 'BV',
  'payment-receipt': 'RP',
  'credit-note': 'NC',
  'client-return-slip': 'BRC',
};

function resolvePdfDocumentPrefix(docType, settings) {
  const key = String(docType || '').trim();
  const cfgKey = {
    invoice: 'invoice',
    proforma: 'proforma',
    'quote-request': 'quoteRequest',
    quote: 'quote',
    'proforma-request': 'proformaRequest',
    'purchase-order': 'order',
    'delivery-note': 'delivery',
    'shipping-slip': 'sendSlip',
    'reception-note': 'reception',
    'return-slip': 'supplierReturnSlip',
    'stock-sheet': 'stockSheet',
    'material-exit': 'delivery',
    'transfer-note': 'transferSlip',
    'exit-note': 'exitSlip',
    'stock-entry': 'delivery',
    'payment-voucher': 'paymentSlip',
    'payment-receipt': 'paymentReceipt',
    'credit-note': 'creditNote',
    'client-return-slip': 'customerReturnSlip',
  }[key];
  if (cfgKey) {
    return getFacturationDocumentPrefix(settings, cfgKey, PDF_DOCUMENT_PREFIX_FALLBACKS[key] || 'DOC');
  }
  return PDF_DOCUMENT_PREFIX_FALLBACKS[key] || 'DOC';
}

const pdfT = (key, opts) => i18n.t(`common.pdf_generator.${key}`, opts);

const formatDateForFileName = (dateString) => {
  if (!dateString) return pdfT('no_date');
  return new Date(dateString).toLocaleDateString(getBrandLocale()).replace(/\//g, '-');
};
// Watermark not needed for now
// import { addWatermarkToPDF } from './watermarkGenerator';

/**
 * TemplateWrapper sets data-paper-size on the preview root (A4 | A5).
 * Maps to valid CSS @page size and physical .invoice-a4-page dimensions for print/PDF.
 */
function getInvoicePrintLayoutFromDataAttr() {
  const el = typeof document !== 'undefined' ? document.querySelector('[data-paper-size]') : null;
  const raw = el ? el.getAttribute('data-paper-size') : 'A4';
  const p = String(raw || 'A4').toLowerCase();
  if (p === 'a5') {
    return { pageSizeCss: 'A5 portrait', width: '148mm', height: '210mm' };
  }
  return { pageSizeCss: 'A4 portrait', width: '210mm', height: '297mm' };
}

// Function to generate client code based on client data
const generateClientCode = (clientData) => {
  if (!clientData) return 'N/A';
  
  // If client has a clientNumber, use it to generate CLI-XX format
  if (clientData.clientNumber) {
    const number = String(clientData.clientNumber).padStart(2, '0');
    return `CLI-${number}`;
  }
  
  // Fallback: use client ID if no clientNumber
  if (clientData.id) {
    const number = String(clientData.id).padStart(2, '0');
    return `CLI-${number}`;
  }
  
  return 'CLI-00';
};

// Function to generate item codes based on category
const generateItemCode = (item, index) => {
  // If no item or empty item, return empty string instead of generating a reference
  if (!item || Object.keys(item).length === 0 || !item.name) {
    return "";
  }

  // If item already has a code, use it - prioritize reference field
  if (item.reference || item.sku || item.itemCode) {
    return item.reference || item.sku || item.itemCode;
  }
  
  // Default categories with prefixes (fallback)
  const defaultCategories = [
    { name: 'Services Informatiques', prefix: 'SRV' },
    { name: 'Matériel Informatiques', prefix: 'MAT' },
    { name: 'Formation', prefix: 'FOR' },
    { name: 'Consulting', prefix: 'CST' },
    { name: 'Maintenance', prefix: 'MNT' }
  ];
  
  // Try to find matching category prefix
  let prefix = 'GEN'; // Default generic prefix
  
  if (item.category) {
    const matchedCategory = defaultCategories.find(cat => 
      cat.name.toLowerCase() === item.category.toLowerCase() ||
      item.category.toLowerCase().includes(cat.name.toLowerCase())
    );
    
    if (matchedCategory) {
      prefix = matchedCategory.prefix;
    }
  }
  
  // If no category match, use basic logic
  if (prefix === 'GEN') {
    const isService = item.type === 'service' || 
                     (item.category && item.category.toLowerCase().includes('service')) ||
                     (item.name && item.name.toLowerCase().includes('service'));
    prefix = isService ? 'SRV' : 'MAT';
  }
  
  const number = String(index + 1).padStart(2, '0');
  return `${prefix}-${number}`;
};

// Function to format bank account
const formatBankAccount = (account) => {
  if (!account || account.trim() === '') return '';
  const parts = account.trim().split(' ');
  if (parts.length < 2) return account.trim();
  
  // Find bank name index
  const bankIndex = parts.findIndex(part => 
    SAUDI_BANK_CODES.includes(part)
  );
  
  if (bankIndex === -1) {
  return account.trim();
  }
  
  const accountNumber = parts.slice(0, bankIndex).join(' ');
  const bankName = parts[bankIndex];
  const location = parts.slice(bankIndex + 1).join(' ');
  
  return `${accountNumber} ${bankName}${location ? ' ' + location : ''}`;
};

// Calculate dynamic column widths based on content
const calculateColumnWidths = (items, pageWidth) => {
  const availableWidth = pageWidth - 40; // margins
  
  // Calculate max item code length
  const maxItemCodeLength = items.length > 0 ? 
    Math.max(...items.map((item, index) => generateItemCode(item, index).length)) : 0;
  
  // Check for wide numbers
  const hasWideNumbers = items.some(item => {
    const quantity = parseInt(item.quantity) || 1;
    return quantity >= 10; // 2+ digits
  });
  
  // Base column widths (percentages)
  let widths = {
    no: 5,      // N°
    item: 10,   // Référence
    desc: 30,   // Désignation
    unit: 5,    // U
    qty: 5,     // Q
    price: 18,  // P.U HT
    amount: 18, // Montant
    tva: 5      // %
  };
  
  // Adjust based on item code length
  if (maxItemCodeLength >= 9) { // very long codes
    widths.item = 14;
    widths.desc = 26;
  } else if (maxItemCodeLength >= 7) { // long codes
    widths.item = 12;
    widths.desc = 28;
  }
  
  // Convert percentages to actual widths
  const totalPercent = Object.values(widths).reduce((a, b) => a + b, 0);
  const actualWidths = {};
  Object.keys(widths).forEach(key => {
    actualWidths[key] = (widths[key] / totalPercent) * availableWidth;
  });
  
  return actualWidths;
};

const ARABIC_PAYMENT_FALLBACKS = {
  prélèvement: 'خصم تلقائي',
  prelevement: 'خصم تلقائي',
  mixte: 'طرق متعددة',
  crédit_documentaire: 'اعتماد مستندي',
  credit_documentaire: 'اعتماد مستندي',
  crédit_compte: 'ائتمان على حساب العميل',
  credit_compte: 'ائتمان على حساب العميل',
  acompte: 'دفعة مقدمة + رصيد',
  livraison: 'عند التسليم',
};

const getPaymentMethodLabel = (method) => {
  if (!method) return '';
  const label = formatPreviewPaymentMethod(method);
  if (label && label !== String(method)) return label;
  const key = String(method).toLowerCase();
  return ARABIC_PAYMENT_FALLBACKS[key] || ARABIC_PAYMENT_FALLBACKS[method] || label || method;
};

// High-quality print-to-PDF generation (same quality as "Imprimer")
export const generateDocumentPDF = async (documentType, formData, clientData, settings, watermark = null) => {
  try {
    // Generate filename first
    const getDocumentPrefix = (docType) => resolvePdfDocumentPrefix(docType, settings);
    
    const prefix = getDocumentPrefix(documentType);
    const dateStr = formatDateForFileName(formData.date);
    const fileName = `${prefix}-${formData.number || 'XXXX'}-${dateStr}.pdf`;

    // Check if browser supports the modern print-to-PDF API
    if (window.showSaveFilePicker && navigator.userAgent.includes('Chrome')) {
      return await generateWithModernAPI(fileName);
    } else {
      // Fallback to window.print() with enhanced styling
      return await generateWithPrintDialog(fileName);
    }
    
  } catch (error) {
    console.error(pdfT('pdf_generation_error'), error);
    
    // Final fallback to high-quality screenshot
    return await generateWithScreenshot(documentType, formData, clientData, settings, null);
  }
};

// Modern print-to-PDF using Chrome's native API (highest quality)
const generateWithModernAPI = async (fileName) => {
  try {
    // Hide action buttons during print
    const actionButtons = document.querySelector('.preview-actions');
    const originalDisplay = actionButtons ? actionButtons.style.display : null;
    if (actionButtons) actionButtons.style.display = 'none';

    // Fix the totals table before printing
    const totalsTable = document.querySelector('.totals-table');
    if (totalsTable) {
      // Force fixed height for all rows
      const rows = totalsTable.querySelectorAll('tr');
      rows.forEach(row => {
        row.style.height = '30px';
        row.style.minHeight = '30px';
        row.style.lineHeight = '30px';
      });

      // Fix cell padding and alignment - CONSISTENT with preview
      const cells = totalsTable.querySelectorAll('td');
      cells.forEach(cell => {
        cell.style.verticalAlign = 'middle';
        cell.style.height = '30px';
        cell.style.lineHeight = '1.5';
        cell.style.display = 'table-cell';
        
        // Apply specific padding based on class
        if (cell.classList.contains('total-label')) {
          cell.style.padding = '0.15rem 0.6rem 0.15rem 1rem';
        } else if (cell.classList.contains('total-value')) {
          cell.style.padding = '0.15rem 1rem';
        }
      });
    }

    const layout = getInvoicePrintLayoutFromDataAttr();

    // Apply print styles temporarily
    const printStyles = document.createElement('style');
    printStyles.textContent = `
      @page {
        size: ${layout.pageSizeCss};
        margin: 0 !important;
      }
      
      @media print {
        body * { visibility: hidden; }
        .document-container, .document-container * { visibility: visible; }
        
        body {
          margin: 0 !important;
          padding: 0 !important;
          background: white !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        
        .document-container { 
          position: absolute; 
          left: 0; 
          top: 0; 
          width: 100%; 
          background: white !important;
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
          margin: 0 !important;
          padding: 0 !important;
        }
        
        .document-paper {
          box-shadow: none !important;
          border-radius: 0 !important;
          margin: 0 auto !important;
          background: white !important;
          width: ${layout.width} !important;
          height: ${layout.height} !important;
          min-height: ${layout.height} !important;
          max-height: ${layout.height} !important;
          padding: 15mm !important;
          page-break-inside: avoid;
          overflow: hidden !important;
          box-sizing: border-box !important;
          font-family: Arial, sans-serif !important;
          font-size: 14px !important;
          line-height: 1.4 !important;
          transform: none !important;
          scale: 1 !important;
        }
        
        .preview-actions,
        .signature-overlay,
        .signature-canvas-overlay {
          display: none !important;
        }
        
                  /* Force blue border color for recipient box */
          .recipient-box {
            border: 1px solid #3b82f6 !important;
            border-color: #3b82f6 !important;
          }
          
          /* Fix for totals table alignment in PDF - CONSISTENT with preview */
          .totals-table td {
            vertical-align: middle !important;
            height: 30px !important;
            min-height: 30px !important;
            line-height: 1.5 !important;
            display: table-cell !important;
          }
          
          .total-label {
            padding: 0.15rem 0.6rem 0.15rem 1rem !important;
            vertical-align: middle !important;
            display: table-cell !important;
            line-height: 1.5 !important;
          }
          
          .total-value {
            padding: 0.15rem 1rem !important;
            vertical-align: middle !important;
            display: table-cell !important;
            line-height: 1.5 !important;
          }

          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
      }
    `;
    document.head.appendChild(printStyles);

    // Use window.print() which triggers the save dialog
    window.print();

    // Clean up
    setTimeout(() => {
      document.head.removeChild(printStyles);
      if (actionButtons && originalDisplay !== null) {
        actionButtons.style.display = originalDisplay;
      } else if (actionButtons) {
        actionButtons.style.display = '';
      }
    }, 1000);

    return true;

  } catch (error) {
    console.error(pdfT('modern_print_api_error'), error);
    throw error;
  }
};

// Enhanced print dialog approach (very high quality)
const generateWithPrintDialog = async (fileName) => {
  try {
    // Create a new window with specific settings to minimize headers/footers
    const printWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=no,resizable=no,toolbar=no,directories=no,location=no,menubar=no,status=no');
    if (!printWindow) {
      throw new Error(pdfT('popup_blocked'));
    }

    // Get the document content (support both old .document-paper and new .document-container)
    const documentElement = document.querySelector('.document-paper') || document.querySelector('.document-container');
    if (!documentElement) {
      throw new Error(pdfT('document_preview_not_found'));
    }

    // Get all CSS stylesheets
    const styles = Array.from(document.styleSheets)
      .map(styleSheet => {
        try {
          return Array.from(styleSheet.cssRules)
            .map(rule => rule.cssText)
            .join('\n');
        } catch (e) {
          return '';
        }
      })
      .join('\n');

    const layout = getInvoicePrintLayoutFromDataAttr();

    // Create clean HTML for printing
    const printHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>${fileName}</title>
        <style>
          ${styles}
          
          @page {
            size: ${layout.pageSizeCss};
            margin: 0 !important;
          }
          
          html {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            width: 100% !important;
            height: 100% !important;
          }
          
          /* Force override browser print margins */
          * {
            box-sizing: border-box !important;
          }
          
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            font-family: Arial, sans-serif;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
            width: 100% !important;
            height: 100% !important;
          }
          
          .document-paper {
            width: ${layout.width} !important;
            height: ${layout.height} !important;
            min-height: ${layout.height} !important;
            max-height: ${layout.height} !important;
            background: white !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            margin: 0 auto !important;
            padding: 15mm !important;
            page-break-inside: avoid;
            overflow: hidden !important;
            box-sizing: border-box !important;
            font-family: Arial, sans-serif !important;
            font-size: 14px !important;
            line-height: 1.4 !important;
            transform: none !important;
            scale: 1 !important;
          }
          
          @media print {
            body { 
              margin: 0 !important; 
              padding: 0 !important; 
              background: white !important;
            }
            .document-paper { 
              margin: 0 auto !important; 
              box-shadow: none !important; 
              border-radius: 0 !important;
              width: ${layout.width} !important;
              height: ${layout.height} !important;
              min-height: ${layout.height} !important;
              max-height: ${layout.height} !important;
              padding: 15mm !important;
              overflow: hidden !important;
              box-sizing: border-box !important;
              font-family: Arial, sans-serif !important;
              transform: none !important;
              scale: 1 !important;
            }
            
            /* Fix for totals table alignment in PDF - CONSISTENT with preview */
            .totals-table td {
              vertical-align: middle !important;
              height: 30px !important;
              min-height: 30px !important;
              line-height: 1.5 !important;
              display: table-cell !important;
            }
            
            .total-label {
              padding: 0.15rem 0.6rem 0.15rem 1rem !important;
              vertical-align: middle !important;
              display: table-cell !important;
              line-height: 1.5 !important;
            }
            
            .total-value {
              padding: 0.15rem 1rem !important;
              vertical-align: middle !important;
              display: table-cell !important;
              line-height: 1.5 !important;
            }
          }
        </style>
      </head>
      <body>
        <div class="document-paper">
          ${documentElement.innerHTML
            .replace(
              /(?:Mode de paiement|طريقة الدفع):\s*([^\s<]+)/g,
              (match, method) => `${pdfT('payment_method_label')} ${getPaymentMethodLabel(method)}`,
            )
            // Fix vertical alignment in totals table by adding extra styling directly to the HTML - CONSISTENT with preview
            .replace(/<table class="totals-table">/g, '<table class="totals-table" style="border-collapse: collapse; width: 100%; table-layout: fixed;">')
            .replace(/<td class="total-label">/g, '<td class="total-label" style="height: 30px; line-height: 1.5; vertical-align: middle; padding: 0.15rem 0.6rem 0.15rem 1rem; display: table-cell;">')
            .replace(/<td class="total-value">/g, '<td class="total-value" style="height: 30px; line-height: 1.5; vertical-align: middle; padding: 0.15rem 1rem; display: table-cell;">')}
        </div>
      </body>
      </html>`;

    // Write content to new window
    printWindow.document.write(printHTML);
    printWindow.document.close();

    return true;

  } catch (error) {
    console.error(pdfT('print_window_error'), error);
    throw error;
  }
};

// Precise PDF generation that captures exactly what's visible in preview
export const generateWithScreenshot = async (documentType, formData, clientData, settings, watermark = null) => {
  try {
    const documentElement = document.querySelector('.document-paper') || document.querySelector('.document-container');
    if (!documentElement) {
      throw new Error(pdfT('document_preview_not_found'));
    }

    console.log('Starting precise PDF generation...');
    
    // Hide UI elements only (no style changes to document)
    const elementsToHide = [
      document.querySelector('.preview-actions'),
      document.querySelector('.signature-overlay'),
      document.querySelector('.signature-canvas-overlay')
    ];
    
    const originalDisplays = elementsToHide.map(el => el ? el.style.display : null);
    elementsToHide.forEach(el => {
      if (el) el.style.display = 'none';
    });

    // Ensure complete font loading and rendering stability
    await document.fonts.ready;
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    // Force reflow to ensure layout is stable
    const forceReflow = documentElement.offsetHeight;
    console.log('Forced reflow, height:', forceReflow);
    
    // Get the exact visual bounds of the document as it appears
    const rect = documentElement.getBoundingClientRect();
    console.log('Document visual bounds:', rect);
    
    // Capture with settings that match browser rendering exactly
    const canvas = await html2canvas(documentElement, {
      scale: 2, // Balanced quality - too high can cause rendering differences
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      removeContainer: false,
      logging: true,
      imageTimeout: 5000, // Longer timeout for complete rendering
      letterRendering: true,
      // Use window dimensions to match browser rendering context
      windowWidth: window.innerWidth,
      windowHeight: window.innerHeight,
      scrollX: 0,
      scrollY: 0,
      onclone: (clonedDoc) => {
        // Ensure the cloned document renders exactly like the original
        const clonedElement = clonedDoc.querySelector('.document-paper') || clonedDoc.querySelector('.document-container');
        if (clonedElement) {
          // Force the same font rendering characteristics
          clonedElement.style.fontSmoothing = 'auto'; // Use browser default
          clonedElement.style.webkitFontSmoothing = 'auto';
          clonedElement.style.textRendering = 'auto';
          
          // Ensure all text elements use browser default rendering
          const allTextElements = clonedElement.querySelectorAll('*');
          allTextElements.forEach(el => {
            el.style.fontSmoothing = 'auto';
            el.style.webkitFontSmoothing = 'auto'; 
            el.style.textRendering = 'auto';
          });
        }
      }
    });

    // Restore UI elements immediately
    elementsToHide.forEach((el, index) => {
      if (el && originalDisplays[index] !== null) {
        el.style.display = originalDisplays[index];
      } else if (el) {
        el.style.display = '';
      }
    });

    // Validate canvas
    if (!canvas || canvas.width === 0 || canvas.height === 0) {
      console.error('Canvas capture failed');
      throw new Error(pdfT('pdf_capture_failed'));
    }
    
    console.log('Canvas captured successfully:', canvas.width, 'x', canvas.height);

    const layout = getInvoicePrintLayoutFromDataAttr();
    const rawAttr = document.querySelector('[data-paper-size]')?.getAttribute('data-paper-size') || 'A4';
    const p = String(rawAttr).toLowerCase();

    const pdfWidth = parseFloat(layout.width);
    const pdfHeight = parseFloat(layout.height);

    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: p === 'a5' ? 'a5' : 'a4' });
    
    // Get high-quality image data
    const imgData = canvas.toDataURL('image/png', 1.0);
    
    // Calculate proper scaling to fill A4 page completely
    const aspectRatio = canvas.width / canvas.height;
    const a4AspectRatio = pdfWidth / pdfHeight;
    
    let finalWidth, finalHeight;
    
    // Fill the entire A4 page while maintaining aspect ratio
    if (aspectRatio > a4AspectRatio) {
      // Document is wider - fit to width, height will be less than A4
      finalWidth = pdfWidth;
      finalHeight = pdfWidth / aspectRatio;
    } else {
      // Document is taller - fit to height, width will be less than A4  
      finalHeight = pdfHeight;
      finalWidth = pdfHeight * aspectRatio;
    }
    
    // Center on A4 page 
    const xOffset = (pdfWidth - finalWidth) / 2;
    const yOffset = (pdfHeight - finalHeight) / 2;
    
    // Add image to PDF with high quality
    pdf.addImage(imgData, 'PNG', xOffset, yOffset, finalWidth, finalHeight, '', 'SLOW');

    // Generate filename
    const getDocumentPrefix = (docType) => resolvePdfDocumentPrefix(docType, settings);
    
    const prefix = getDocumentPrefix(documentType);
    const dateStr = formatDateForFileName(formData.date);
    const fileName = `${prefix}-${formData.number || 'XXXX'}-${dateStr}.pdf`;
    
    // CRITICAL: Add watermark if provided (for accounting reports - Decret 09-110 compliance)
    if (watermark) {
      addWatermarkToPDF(pdf, watermark);
    }
    
    pdf.save(fileName);
    
    return true;
    
  } catch (error) {
    console.error(pdfT('pdf_generation_error'), error);
    throw error;
  }
};





// Download function for direct file download
export const downloadDocumentPDF = async (documentType, formData, clientData, settings, watermark = null) => {
  try {
    // Build filename
    const prefix = resolvePdfDocumentPrefix(documentType, settings);
    const dateStr = formatDateForFileName(formData?.date);
    // formData.number already contains the prefix (e.g. "BC-01"), so don't prepend it again
    const docNumber = formData?.number || `${prefix}-XXXX`;
    const fileName = `${docNumber}-${dateStr}.pdf`;

    // --- New-template path: use browser print engine (PERFECT QUALITY) ---
    // window.print() provides perfect vector quality with proper fonts

    const a4PagesFallback = document.querySelectorAll('.invoice-a4-page');
    if (a4PagesFallback.length > 0) {
      const styles = Array.from(document.styleSheets)
        .map(sheet => {
          try { return Array.from(sheet.cssRules).map(r => r.cssText).join('\n'); }
          catch { return ''; }
        })
        .join('\n');

      const pagesHTML = Array.from(a4PagesFallback).map(p => p.outerHTML).join('\n');
      const layout = getInvoicePrintLayoutFromDataAttr();

      const printWindow = window.open('', '_blank', 'width=900,height=700');
      if (!printWindow) {
        return await generateWithScreenshot(documentType, formData, clientData, settings, watermark);
      }

      const printHTML = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${fileName}</title>
  <style>
    ${styles}
    @page { size: ${layout.pageSizeCss}; margin: 0mm !important; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0 !important; padding: 0 !important; background: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .invoice-a4-page {
      width: ${layout.width} !important;
      min-height: ${layout.height} !important;
      box-shadow: none !important;
      border-radius: 0 !important;
      margin: 0 !important;
      page-break-after: always;
    }
    .invoice-a4-page:last-child {
      page-break-after: auto;
    }
  </style>
</head>
<body>
  <div class="document-container">
    ${pagesHTML}
  </div>
  <script>
    window.onload = function() {
      document.title = '${fileName}';
      setTimeout(function() {
        window.print();
      }, 1000);
    };
  </script>
</body>
</html>`;

      printWindow.document.write(printHTML);
      printWindow.document.close();
      return;
    }

    // --- Legacy path: old .document-paper templates ---
    return await generateWithPrintDialog(fileName);

  } catch (error) {
    console.error(pdfT('pdf_download_error'), error);
    throw error;
  }
};

/**
 * Merged bulk print — même rendu que le flux `.invoice-a4-page`, mais impression via iframe
 * cachée pour éviter d’ouvrir un deuxième onglet / fenêtre (un seul contexte navigateur pour l’utilisateur).
 */
export const printMergedInvoicePagesHTML = (pagesHTML, documentTitle = 'Documents') => {
  try {
    if (!pagesHTML || typeof pagesHTML !== 'string' || !pagesHTML.trim()) {
      console.warn('printMergedInvoicePagesHTML: empty pagesHTML');
      return false;
    }

    const styles = Array.from(document.styleSheets)
      .map(sheet => {
        try {
          return Array.from(sheet.cssRules).map(r => r.cssText).join('\n');
        } catch {
          return '';
        }
      })
      .join('\n');

    const layout = getInvoicePrintLayoutFromDataAttr();
    const titleEsc = String(documentTitle || 'Documents')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .slice(0, 220);

    const printHTML = `<!DOCTYPE html>
<html lang="fr">
<head>
  <meta charset="UTF-8">
  <title>${titleEsc}</title>
  <style>
    ${styles}
    @page { size: ${layout.pageSizeCss}; margin: 0mm !important; }
    * { box-sizing: border-box; }
    html, body {
      margin: 0 !important; padding: 0 !important; background: white;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
    .invoice-a4-page {
      width: ${layout.width} !important;
      min-height: ${layout.height} !important;
      box-shadow: none !important;
      border-radius: 0 !important;
      margin: 0 !important;
      page-break-after: always;
    }
    .invoice-a4-page:last-child {
      page-break-after: auto;
    }
    .document-container {
      overflow: visible !important; max-height: none !important;
      padding: 0 !important; gap: 0 !important; background: white !important;
    }
  </style>
</head>
<body>
  <div class="document-container">
${pagesHTML}
  </div>
</body>
</html>`;

    const iframe = document.createElement('iframe');
    iframe.setAttribute(
      'title',
      String(documentTitle || 'Documents').slice(0, 120),
    );
    Object.assign(iframe.style, {
      position: 'fixed',
      width: '0',
      height: '0',
      bottom: '0',
      right: '0',
      border: '0',
      visibility: 'hidden',
    });

    document.body.appendChild(iframe);

    const idoc =
      iframe.contentDocument || iframe.contentWindow?.document;
    if (!idoc) {
      iframe.remove();
      return false;
    }

    idoc.open();
    idoc.write(printHTML);
    idoc.close();

    const runPrintOnce = () => {
      const teardown = () => {
        try {
          iframe.remove();
        } catch {
          /* ignore */
        }
      };

      const w = iframe.contentWindow;
      if (!w?.print) {
        teardown();
        return;
      }

      w.addEventListener('afterprint', teardown, { once: true });
      setTimeout(teardown, 120000);

      w.focus();
      w.print();
    };

    setTimeout(runPrintOnce, 500);
    return true;
  } catch (error) {
    console.error('printMergedInvoicePagesHTML:', error);
    return false;
  }
};

// ─── helpers ──────────────────────────────────────────────────────────────────

const buildFileName = (documentType, formData, settings) => {
  const p = (docKey, fallback) => getFacturationDocumentPrefix(settings, docKey, fallback);
  const prefixMap = {
    'invoice': p('invoice', 'FAC'),
    'proforma': p('proforma', 'PRO'),
    'quote': p('quote', 'DEV'),
    'quote-request': p('quoteRequest', 'DD'),
    'proforma-request': p('proformaRequest', 'DP'),
    'purchase-order': p('order', 'BC'),
    'delivery-note': p('delivery', 'BL'),
    'shipping-slip': p('sendSlip', 'BE'),
    'reception-note': p('reception', 'BR'),
    'return-slip': p('return', 'RT'),
    'material-exit': p('delivery', 'BL'),
    'transfer-note': p('transferNote', 'BT'),
    'exit-note': p('exitNote', 'BS'),
    'stock-entry': p('stockEntry', 'BE'),
    'payment-voucher': p('paymentSlip', 'BV'),
    'payment-receipt': p('paymentReceipt', 'RG'),
    'credit-note': p('creditNote', 'AV'),
    'client-return-slip': p('customerReturnSlip', 'RC'),
  };
  const prefix = prefixMap[documentType] || 'DOC';
  const dateStr = formatDateForFileName(formData?.date);
  // formData.number already contains the prefix (e.g. "DD-01"), so don't prepend it again
  const docNumber = formData?.number || `${prefix}-XXXX`;
  return `${docNumber}-${dateStr}.pdf`;
};

const buildPrintHTML = (fileName) => {
  const a4Pages = document.querySelectorAll('.invoice-a4-page');
  if (!a4Pages.length) return null;

  const layout = getInvoicePrintLayoutFromDataAttr();

  const styles = Array.from(document.styleSheets)
    .map(sheet => {
      try { return Array.from(sheet.cssRules).map(r => r.cssText).join('\n'); }
      catch { return ''; }
    })
    .join('\n');

  const pagesHTML = Array.from(a4Pages).map(p => p.outerHTML).join('\n');

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>${fileName}</title>
  <style>
    ${styles}
    @page { size: ${layout.pageSizeCss}; margin: 0mm !important; }
    @page :first { margin: 0mm !important; }
    @page :left  { margin: 0mm !important; }
    @page :right { margin: 0mm !important; }
    * { box-sizing: border-box; }
    html, body { margin: 0 !important; padding: 0 !important; background: white;
      -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    .invoice-a4-page {
      width: ${layout.width} !important; min-height: ${layout.height} !important;
      box-shadow: none !important; border-radius: 0 !important;
      margin: 0 !important; page-break-after: always; break-after: page;
    }
    .invoice-a4-page:last-child { page-break-after: auto; break-after: auto; }
    .document-container {
      overflow: visible !important; max-height: none !important;
      padding: 0 !important; gap: 0 !important; background: white !important;
    }
  </style>
</head>
<body>
  <div class="document-container">${pagesHTML}</div>
</body>
</html>`;
};

// ─── Silent download (Electron EXE) ──────────────────────────────────────────
// When running inside Electron, uses webContents.printToPDF() → saves directly
// to the user's Downloads folder with no dialog. Falls back to print window in browser.
export const downloadDocumentPDFSilent = async (documentType, formData, clientData, settings, watermark = null) => {
  const fileName = buildFileName(documentType, formData, settings);

  // ── Electron path: silent, vector-quality, no dialog ──────────────────────
  if (window.electronAPI?.savePDF) {
    const htmlContent = buildPrintHTML(fileName);
    if (htmlContent) {
      const result = await window.electronAPI.savePDF({ htmlContent, fileName });
      if (result?.success) {
        // Small toast-style notification so the user knows where the file went
        const toast = document.createElement('div');
        toast.textContent = pdfT('pdf_saved_downloads', { fileName });
        Object.assign(toast.style, {
          position: 'fixed', bottom: '24px', right: '24px', zIndex: 99999,
          background: '#166534', color: '#fff', padding: '12px 20px',
          borderRadius: '10px', fontSize: '14px', boxShadow: '0 4px 16px rgba(0,0,0,.3)',
          transition: 'opacity .4s'
        });
        document.body.appendChild(toast);
        setTimeout(() => { toast.style.opacity = '0'; setTimeout(() => toast.remove(), 400); }, 4000);
        return;
      }
      // If printToPDF failed for any reason, fall through to print window below
      console.warn('Electron printToPDF failed, falling back to print dialog:', result?.error);
    }
  }

  // ── Browser fallback: open print window (same as downloadDocumentPDF) ──────
  return downloadDocumentPDF(documentType, formData, clientData, settings, watermark);
};

// ─── Clean preview ────────────────────────────────────────────────────────────
// Clean preview function for consistency with PDF generation
export const previewDocumentPDF = async (documentType, formData, clientData, settings) => {
  try {
    const documentElement = document.querySelector('.document-paper') || document.querySelector('.document-container');
    if (!documentElement) {
      throw new Error(pdfT('document_preview_not_found'));
    }

    // Wait for fonts to load
    await document.fonts.ready;
    await new Promise(resolve => setTimeout(resolve, 200));

    // Capture with clean settings matching PDF generation
    const canvas = await html2canvas(documentElement, {
      scale: 2, // Consistent with PDF generation approach
      useCORS: true,
      allowTaint: true,
      backgroundColor: '#ffffff',
      removeContainer: false,
      logging: false,
      imageTimeout: 2000,
      letterRendering: true,
      // Use original dimensions for consistency
      width: documentElement.scrollWidth,
      height: documentElement.scrollHeight,
      scrollX: 0,
      scrollY: 0
    });

    return canvas.toDataURL('image/png', 1.0);
    
  } catch (error) {
    console.error(pdfT('pdf_preview_error'), error);
    throw error;
  }
}; 
